import { Contact } from '@rentguru/commons-utils';
import { get } from '@up2rent/graphql';
import { isNil } from 'lodash';

const ACCESS_SYNC_WORKER_MAX_DURATION_MS = 5 * 60 * 1000;
const ACCESS_SYNC_PROBE_DELAY_MS = 2000;
const ACCESS_SYNC_PROBE_MAX_ATTEMPTS = ACCESS_SYNC_WORKER_MAX_DURATION_MS / ACCESS_SYNC_PROBE_DELAY_MS;

const CONTACT_READ_ACCESS_PROBE = /* GraphQL */ `
  query GetContact($id: ID!) {
    getContact(id: $id) {
      id
    }
  }
`;

const delay = (durationMs: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, durationMs));

export const waitForNextAccessProbe = (): Promise<void> => delay(ACCESS_SYNC_PROBE_DELAY_MS);

export interface ReadAccessProbeOutcome<ResultType> {
  granted: boolean;
  result: ResultType;
}

export const waitForReadAccess = async <ResultType>(
  probe: () => Promise<ResultType>,
  hasReadAccess: (result: ResultType) => boolean,
  signal?: AbortSignal
): Promise<ReadAccessProbeOutcome<ResultType>> => {
  let result = await probe();
  let attempts = 1;
  while (!hasReadAccess(result) && attempts < ACCESS_SYNC_PROBE_MAX_ATTEMPTS && !signal?.aborted) {
    await delay(ACCESS_SYNC_PROBE_DELAY_MS);
    if (signal?.aborted) {
      break;
    }
    result = await probe();
    attempts += 1;
  }
  return { granted: hasReadAccess(result), result };
};

const probeContactReadAccess = async (contactId: string): Promise<Contact | undefined> => {
  try {
    return await get<Contact>(CONTACT_READ_ACCESS_PROBE, contactId);
  } catch {
    return undefined;
  }
};

export const waitForContactReadAccess = async (contactId: string): Promise<boolean> => {
  const { granted } = await waitForReadAccess<Contact | undefined>(
    () => probeContactReadAccess(contactId),
    (contact) => !isNil(contact)
  );
  return granted;
};
